import { motion } from "framer-motion";

/**
 * Premium animated "3D-feel" hand — pure SVG.
 * Layered wireframe + neon glow + finger flex + slow rotate.
 * Interactive: hover/click.
 */
export default function AnimatedHand({ size = 340, onClick, testId, floating = false }) {
  const strokeGrad = "handStroke";
  const glowGrad = "handGlow";
  const wireGrad = "handWire";
  const idSuffix = floating ? "-f" : "-m";

  return (
    <motion.button
      data-testid={testId}
      onClick={onClick}
      whileHover={{ scale: floating ? 1.08 : 1.04 }}
      whileTap={{ scale: 0.95 }}
      className="relative outline-none border-0 bg-transparent cursor-pointer group"
      style={{ width: size, height: size }}
      aria-label="Open"
    >
      {/* Orbit rings */}
      {!floating && (
        <>
          <div className="absolute inset-0 orbit-slow pointer-events-none">
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{
                width: size * 0.98,
                height: size * 0.98,
                border: "1px dashed rgba(0, 240, 255, 0.18)",
              }}
            />
          </div>
          <div className="absolute inset-0 orbit-fast pointer-events-none">
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{
                width: size * 0.78,
                height: size * 0.78,
                border: "1px dotted rgba(112, 0, 255, 0.24)",
              }}
            />
          </div>
        </>
      )}

      <div className="absolute inset-0 breath slowrotate">
        <svg
          viewBox="0 0 200 220"
          className="w-full h-full hand-orb"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id={strokeGrad + idSuffix} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#00F0FF" />
              <stop offset="100%" stopColor="#7000FF" />
            </linearGradient>
            <radialGradient id={glowGrad + idSuffix} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="rgba(0,240,255,0.55)" />
              <stop offset="55%" stopColor="rgba(112,0,255,0.25)" />
              <stop offset="100%" stopColor="rgba(0,0,0,0)" />
            </radialGradient>
            <linearGradient id={wireGrad + idSuffix} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="rgba(0,240,255,0.9)" />
              <stop offset="100%" stopColor="rgba(255,46,147,0.7)" />
            </linearGradient>
            <filter id={"blur" + idSuffix}>
              <feGaussianBlur stdDeviation="2.4" />
            </filter>
          </defs>

          {/* Inner palm glow */}
          <circle cx="100" cy="130" r="42" fill={`url(#${glowGrad}${idSuffix})`} />

          {/* Palm */}
          <path
            d="M60 200 Q56 128 78 92 Q88 74 102 78 Q118 82 124 104 Q140 100 146 118 Q158 140 148 176 Q142 200 132 208 Z"
            fill="rgba(10,10,20,0.75)"
            stroke={`url(#${strokeGrad}${idSuffix})`}
            strokeWidth="1.2"
          />

          {/* Wire mesh on palm */}
          <g opacity="0.55" stroke={`url(#${wireGrad}${idSuffix})`} strokeWidth="0.5" fill="none">
            <path d="M62 190 Q100 150 148 172" />
            <path d="M64 170 Q100 130 146 148" />
            <path d="M70 148 Q100 116 140 130" />
            <path d="M78 128 Q100 100 132 114" />
            <path d="M82 108 Q100 92 122 100" />
            <path d="M100 78 L102 200" />
            <path d="M84 88 L128 200" />
          </g>

          {/* Wrist ring */}
          <ellipse
            cx="100"
            cy="208"
            rx="42"
            ry="6"
            fill="none"
            stroke={`url(#${strokeGrad}${idSuffix})`}
            strokeWidth="1.2"
          />
          <ellipse
            cx="100"
            cy="212"
            rx="46"
            ry="4"
            fill="none"
            stroke="rgba(0,240,255,0.35)"
            strokeWidth="0.7"
          />

          {/* Fingers */}
          {/* Index */}
          <g className="finger-1">
            <rect x="88" y="34" width="10" height="60" rx="5" fill="rgba(10,10,20,0.9)" stroke={`url(#${strokeGrad}${idSuffix})`} strokeWidth="1.1" />
            <circle cx="93" cy="34" r="4.5" fill="rgba(0,240,255,0.9)" filter={`url(#blur${idSuffix})`} />
          </g>
          {/* Middle */}
          <g className="finger-2">
            <rect x="100" y="22" width="10" height="72" rx="5" fill="rgba(10,10,20,0.9)" stroke={`url(#${strokeGrad}${idSuffix})`} strokeWidth="1.1" />
            <circle cx="105" cy="22" r="4.5" fill="rgba(0,240,255,0.9)" filter={`url(#blur${idSuffix})`} />
          </g>
          {/* Ring */}
          <g className="finger-3">
            <rect x="112" y="30" width="10" height="66" rx="5" fill="rgba(10,10,20,0.9)" stroke={`url(#${strokeGrad}${idSuffix})`} strokeWidth="1.1" />
            <circle cx="117" cy="30" r="4.5" fill="rgba(255,46,147,0.9)" filter={`url(#blur${idSuffix})`} />
          </g>
          {/* Pinky */}
          <g className="finger-4">
            <rect x="124" y="46" width="9" height="52" rx="4.5" fill="rgba(10,10,20,0.9)" stroke={`url(#${strokeGrad}${idSuffix})`} strokeWidth="1.1" />
            <circle cx="128.5" cy="46" r="4" fill="rgba(112,0,255,0.9)" filter={`url(#blur${idSuffix})`} />
          </g>
          {/* Thumb */}
          <g>
            <path
              d="M64 122 Q48 110 52 92 Q56 78 72 82 Q80 84 82 96 L80 130 Z"
              fill="rgba(10,10,20,0.9)"
              stroke={`url(#${strokeGrad}${idSuffix})`}
              strokeWidth="1.1"
            />
            <circle cx="60" cy="94" r="4.5" fill="rgba(0,240,255,0.9)" filter={`url(#blur${idSuffix})`} />
          </g>

          {/* Central pulse node */}
          <circle cx="100" cy="140" r="6" fill="#00F0FF">
            <animate attributeName="r" values="5;9;5" dur="2.2s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="1;0.4;1" dur="2.2s" repeatCount="indefinite" />
          </circle>
        </svg>
      </div>
    </motion.button>
  );
}
